import {BLANK_MAC_VALUE} from "../constant/appConstants";

export const DEVICE_STATUS = Object.freeze({
    new: 'new',
    known: 'known'
});

export const DEFAULT_METADATA = Object.freeze({
    /**
     * A value of {@link DEVICE_STATUS}
     */
    status: Object.freeze(''),
    /**
     * Fields with associated edits
     */
    edits: Object.freeze({}),
    /**
     * Map of errors from server validation
     */
    errorMap: Object.freeze(undefined)
});

/**
 * Default values applied to all normalized devices
 *
 * @type {Readonly<{mac: string}>}
 */
export const DEFAULT_DEVICE_VALUES = Object.freeze({
    mac: '____________'
});

export const getEpNumber = (device) => {
    if (device.epNumber) {
        return device.epNumber > 9 ? device.epNumber : "0" + device.epNumber;
    } else {
        return "--";
    }
};

export const sortByEpId = (device1, device2) => {
    try {
        return parseInt(getEpNumber(device1), 10) - parseInt(getEpNumber(device2), 10);
    } catch (error) {
        return 0;
    }
};

/**
 * Normalizes devices with app metadata. Will merge current {@link DEFAULT_METADATA} shape with existing device metadata for
 * each device along with any overwrites passed in for the devices.
 *
 * @param list of devices to be normalized
 * @param state containing any existing device information
 * @param overwrites for the metadata fields
 * @returns {{}}
 */
export const normalizeDevices = function (list, state, overwrites) {
    if (list === undefined) return {};
    let output = {};
    list.forEach(device => {
        let merge = Object.assign({}, DEFAULT_METADATA);
        if (device) {
            merge = Object.assign(merge, device.metadata);
        }
        if (state[device.equipDetailsId]) {
            merge = Object.assign(merge, state[device.equipDetailsId].metadata);
        }
        output[device.equipDetailsId] = {
            // TODO: should you merge current app data on normalize?
            ...state[device.equipDetailsId],
            ...DEFAULT_DEVICE_VALUES,
            ...device,
            metadata: {
                ...merge,
                ...overwrites
            }
        };
    });
    return output;
};

/**
 * Clone and scrub stateful data that should not be persisted in the local database.
 *
 * JSON functions used to provide a reference free deep copy but should be noted that any functions in the objects will
 * also be converted to strings (lost) in this process. The device objects should not run in to this as they are data only.
 *
 * @param device
 * @returns {{}}
 */
export const scrubDevice = function (device) {
    const clone = JSON.parse(JSON.stringify(device));
    delete clone.metadata.errorMap;
    return clone;
};

export const isEditing = function (device) {
    return device.metadata.status === DEVICE_STATUS.new || (Object.keys(device.metadata.edits).length > 0);
};

export const isFiltering = function (device, isReplaceableDevice, devices){
    if(!isReplaceableDevice){
        return device.metadata.status === DEVICE_STATUS.new || (Object.keys(device.metadata.edits).length > 0);
    }

    const deviceWithSameEPNumberAndWithoutMacAddress = Object.values(devices)
        .filter(d => d.mac === BLANK_MAC_VALUE && d.epNumber === device.epNumber)[0];

    return deviceWithSameEPNumberAndWithoutMacAddress && (Object.keys(deviceWithSameEPNumberAndWithoutMacAddress.metadata.edits).length > 0);
}

export const MAC_REGEX = /^(([0-9A-Fa-f]{2}[:-]?){6})$/;

export const validateMacFormat = function (mac) {
    return MAC_REGEX.test(mac);

};

/**
 * Validates the uniqueness of a MAC address within the current site.
 *
 * @returns {boolean} true if the MAC is unique to the site
 */
export const validateMacUnique = function (mac, allDevices) {
    let macList = [];
    Object.keys(allDevices).forEach(key => {
        if (allDevices[key].mac && !allDevices[key].mac.includes('_')) {
            macList.push(allDevices[key].mac.toUpperCase());
        }
        if (allDevices[key].metadata.edits.mac && !allDevices[key].metadata.edits.mac.includes('_')) {
            macList.push(allDevices[key].metadata.edits.mac.toUpperCase());
        }
    });
    return !macList.includes(mac.toUpperCase());
};


// WEBPACK FOOTER //
// ./src/util/DeviceUtils.js